import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/services/api';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'cashier' | 'manager';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  vendorId?: string;
  tenantId?: string;
  businessId?: string;
  branchId?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshMe: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      setUser: (user: User | null) => {
        set({ user });
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          // Try actual API call
          const response = await api.post('/api/auth/login', {
            email,
            password,
          });

          const { token, user } = response.data;
          
          // Set token in API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      refreshMe: async () => {
        const { token } = get();
        if (!token) throw new Error('Not authenticated');

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/api/auth/me');
        const { user: userData } = response.data;

        set({
          user: userData,
          isAuthenticated: true,
        });
      },

      logout: async () => {
        try {
          await api.post('/api/auth/logout');
        } catch (error) {
          // Ignore logout errors
        } finally {
          // Clear token from API headers
          delete api.defaults.headers.common['Authorization'];
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      checkAuth: async () => {
        const { token, user, isAuthenticated } = get();

        // If we already have a user and are authenticated, no need to check further
        if (user && isAuthenticated) {
          return;
        }
        
        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }

        set({ isLoading: true });
        
        try {
          // Set token in API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          const response = await api.get('/api/auth/me');
          const { user: userData } = response.data;
          
          set({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // Token is invalid, clear auth state
          delete api.defaults.headers.common['Authorization'];
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      updateProfile: async (data: Partial<User>) => {
        const { user } = get();
        if (!user) throw new Error('Not authenticated');

        const response = await api.put(`/users/${user.id}`, data);
        const updatedUser = response.data.user;
        
        set({
          user: updatedUser,
        });
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        await api.post('/api/auth/change-password', {
          currentPassword,
          newPassword,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
